import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categoryMap } from '@/mocks/movies';

export default function MovieDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const movie = movies.find(m => m.slug === slug);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <span className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mx-auto mb-4">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">电影未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的电影页面不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = movies
    .filter(m => m.category === movie.category && m.id !== movie.id)
    .slice(0, 6);

  const categorySlug = Object.keys(categoryMap).find(k => categoryMap[k] === movie.category) || 'all';

  const formatViews = (views: number) => {
    if (views >= 10000000) return `${(views / 10000000).toFixed(1)}千万`;
    if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
    return views.toString();
  };

  return (
    <>
      <Navbar />

      <main>
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: movie.category, href: `/category/${categorySlug}` },
                { label: movie.title }
              ]}
            />
          </div>
        </div>

        {/* Movie Detail Hero */}
        <article className="relative">
          <div className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
            <img
              src={movie.coverUrl}
              alt={movie.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/50 to-background-950/30"></div>
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="relative -mt-32 md:-mt-40 z-10 flex flex-col md:flex-row gap-6 md:gap-8 pb-12">
              <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden shadow-2xl">
                  <img
                    src={movie.posterUrl}
                    alt={`${movie.title} 海报`}
                    title={`${movie.title} - 屁屁影院高清在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 text-center md:text-left pt-2">
                <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start mb-3">
                  {movie.isNew && (
                    <span className="px-3 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full whitespace-nowrap">最新</span>
                  )}
                  {movie.isVip && (
                    <span className="px-3 py-1 text-xs font-medium bg-accent-500 text-background-50 rounded-full whitespace-nowrap">VIP</span>
                  )}
                  <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">{movie.category}</span>
                </div>

                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                  {movie.title}
                </h1>
                <p className="text-sm text-background-300 mb-2">{movie.titleEn} · {movie.year}</p>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 justify-center md:justify-start mb-6 text-sm text-background-300">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-accent-500 font-bold text-lg">{movie.rating.toFixed(1)}</strong>
                  </span>
                  <span><i className="ri-time-line mr-1 text-background-400"></i>{movie.duration}</span>
                  <span><i className="ri-earth-line mr-1 text-background-400"></i>{movie.region}</span>
                  <span><i className="ri-eye-line mr-1 text-background-400"></i>{formatViews(movie.views)} 次播放</span>
                </div>

                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill text-lg"></i>
                    立即播放
                  </span>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Movie Info */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
              <div className="lg:col-span-2">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">剧情简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{movie.description}</p>
              </div>

              <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">影片信息</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <dt className="text-foreground-500">导演</dt>
                    <dd className="text-foreground-800 font-medium">{movie.director}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-foreground-500">主演</dt>
                    <dd className="text-foreground-800 font-medium text-right">{movie.cast.join(' / ')}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-foreground-500">类型</dt>
                    <dd className="text-foreground-800 font-medium">{movie.category}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-foreground-500">年份</dt>
                    <dd className="text-foreground-800 font-medium">{movie.year}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-foreground-500">地区</dt>
                    <dd className="text-foreground-800 font-medium">{movie.region}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-foreground-500">时长</dt>
                    <dd className="text-foreground-800 font-medium">{movie.duration}</dd>
                  </div>
                  <div className="flex justify-between text-sm">
                    <dt className="text-foreground-500">评分</dt>
                    <dd className="text-accent-500 font-bold">{movie.rating.toFixed(1)}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </section>

        {/* Related Movies */}
        {relatedMovies.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100" aria-label="相关推荐">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">相关推荐</h2>
                  <p className="text-sm text-foreground-500 mt-1">同类型热门电影推荐</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {relatedMovies.map(m => (
                  <MovieCard
                    key={m.id}
                    id={m.id}
                    slug={m.slug}
                    title={m.title}
                    year={m.year}
                    category={m.category}
                    rating={m.rating}
                    posterUrl={m.posterUrl}
                    isVip={m.isVip}
                    isNew={m.isNew}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Movie",
            "name": movie.title,
            "description": movie.description,
            "image": movie.posterUrl,
            "dateCreated": `${movie.year}`,
            "director": { "@type": "Person", "name": movie.director },
            "actor": movie.cast.map(name => ({ "@type": "Person", "name": name })),
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": movie.rating.toFixed(1),
              "bestRating": "10",
              "ratingCount": movie.views
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.pipicinema.com" },
                { "@type": "ListItem", "position": 2, "name": movie.category, "item": `https://www.pipicinema.com/category/${categorySlug}` },
                { "@type": "ListItem", "position": 3, "name": movie.title }
              ]
            }
          })
        }}
      />
    </>
  );
}